(ns arthur.domain.creation
  "Resolve where a new thing goes from the primary selection and playhead.

  This namespace owns no editor state. A row address is a preference; walking
  the occurrence path at one frame answers which preferred or enclosing symbol
  is actually available. See `docs/creating-in.md`."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]
            [arthur.domain.symbol :as symbol]))

(defn- path-node
  "The node at the end of an occurrence `path`, walked from `open`.

  Selection also carries an owner sid and node id for commands that edit the
  node directly. Those are deliberately not used here: the path is the address
  of the row as seen from the open symbol, and is the only part that describes
  every enclosing occurrence at arbitrary depth."
  [document open path]
  (loop [sid open [id & more] (seq path) found nil]
    (if-not id
      found
      (when-let [n (get-in document [:symbols sid :nodes id])]
        (if (seq more)
          (when-let [inner (and (= :instance (:kind n)) (node/source n))]
            (recur inner more n))
          n)))))

(defn preferred-path
  "The container path structurally implied by `selection`.

  Selecting an instance means inside it. Selecting any other node means its
  containing symbol. An empty or non-node selection means the open symbol."
  [document open selection]
  (let [[kind _sid id selected-path] selection
        path (when (= :node kind)
               (vec (or (seq selected-path) (when id [id]))))
        selected (path-node document open path)]
    (cond
      (empty? path) []
      (= :instance (:kind selected)) path
      :else (vec (butlast path)))))

(defn target
  "The nearest creation context available at `frame` of `open`.

  Every non-empty candidate is validated by `nest/inside`, so every enclosing
  symbol occurrence must be under the playhead in the current context. Walking
  outward stops at the first valid symbol. A lane needs no cel at that frame,
  but the occurrence chain that reaches the lane must still be valid. The empty
  path always resolves to the open symbol.

  A tracing symbol is never one: it holds a picture to draw over and no nodes,
  so selecting a tracing layer creates beside it, as selecting a shape does.

  Returns `{:kind :lane|:symbol :sid :path :frame :matrix :time}`."
  [document store open selection frame]
  (let [preferred (preferred-path document open selection)]
    (some (fn [path]
            (when-let [inside (nest/inside document store open path frame)]
              (when-let [sid (and (:sid inside)
                                  (not (clip/trace? (clip/symbol document (:sid inside))))
                                  (:sid inside))]
                (assoc inside :path path
                       :kind (if (symbol/lane? (clip/symbol document sid))
                               :lane :symbol)))))
          (take (inc (count preferred)) (iterate pop preferred)))))
